import { FC, useState } from 'react'
import { Avatar, Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { DeleteOutline } from '@mui/icons-material'
import { useIntl } from 'react-intl'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { DeactivateAccount } from 'components/DeactivateAccount'
import { useUsersProfile } from 'services/profile/profile.hooks'
import theme from 'styles/theme'

import UserPassword from './UserPassword'

const deactivateButtonSx = {
  backgroundColor: theme.r4r.red,
  color: '#fff',
  borderRadius: 0,
  boxShadow: 'none',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  lineHeight: '13px',
  textTransform: 'uppercase' as const,
  padding: '14px 24px',
  ':hover': {
    backgroundColor: '#DC2626',
    boxShadow: 'none'
  }
}

const UserAccount: FC = () => {
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { data: user, userInitials } = useUsersProfile()
  const { formatMessage } = useIntl()
  const [accountView, setAccountView] = useState<'menu' | 'password'>('menu')

  if (accountView === 'password') {
    return <UserPassword onBack={() => setAccountView('menu')} />
  }

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
      <Box sx={{ width: '100%', maxWidth: '500px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: '24px'
          }}>
          <Avatar
            sx={{
              width: '96px',
              height: '96px',
              background: theme.r4r.gradient,
              color: '#fff',
              fontSize: '28px',
              fontWeight: 800
            }}>
            {userInitials}
          </Avatar>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: theme.r4r.navy,
              mt: '12px',
              textAlign: 'center'
            }}>
            {user?.firstName} {user?.lastName}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.r4r.grey,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            mb: '6px'
          }}>
          {formatMessage({ id: 'userAccount.emailAddress', defaultMessage: 'Adres e-mail' })}
        </Typography>
        <Box
          sx={{
            backgroundColor: '#f9fafb',
            border: `1px solid ${theme.r4r.greyLight}`,
            px: '17px',
            py: '15px'
          }}>
          <Typography sx={{ fontSize: '14px', color: theme.r4r.grey }}>
            {user?.email}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '16px' }}>
          <Button
            onClick={() => setAccountView('password')}
            sx={{
              color: theme.r4r.azure,
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              lineHeight: '13px',
              textTransform: 'uppercase',
              padding: 0,
              minWidth: 0,
              ':hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
            }}>
            {formatMessage({
              id: 'userPassword.changePassword',
              defaultMessage: 'Zmień hasło'
            })}
          </Button>
        </Box>

        <Box sx={{ borderTop: `1px solid ${theme.r4r.greyLight}`, mt: '24px', pt: '34px' }}>
          <DeactivateAccount
            startIcon={<DeleteOutline sx={{ fontSize: '16px' }} />}
            sx={deactivateButtonSx}
          />
        </Box>
      </Box>
    </Box>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({ id: 'userAccount.label', defaultMessage: 'Zarządzanie kontem' })}
      />
      <BackNavigationContent>{content}</BackNavigationContent>
    </>
  )
}

export default UserAccount
